import { useZero } from "@rocicorp/zero/react";
import { type FormEvent, useState } from "react";
import { MULTI_IDP_SETTINGS_FLAG } from "../../../../shared/featureFlagNames";
import { jitUserProvisioningEnabled } from "../../../../shared/jitProvisioning";
import { sharedMutators } from "../../../../shared/mutators";
import { ssoIssuerUrlError } from "../../../../shared/ssoIssuerUrl";
import { useFeatureFlag } from "../../../hooks/useFeatureFlag";
import { Box } from "../../Box";
import { Button } from "../../Button";
import { Input } from "../../Input";
import { Text } from "../../Text";
import { emptyMapping, emptySsoConfig, parseScopes } from "./constants";
import { ScimTokenSection } from "./GroupSyncSection";
import { newScimToken } from "./scimToken";
import { SsoProviderFormFields } from "./SsoProviderFormFields";
import type { SyncMode } from "./SyncModeFields";
import { Field, FormActions, Section } from "./shared";

const disabledStyle = {
  width: "100%",
  backgroundColor: "var(--alpha4)",
  color: "var(--text-secondary)",
};

export function ExistingSsoProvider({
  provider,
  onGeneratedScimToken,
}: {
  provider: {
    id: string;
    providerId: string;
    issuer: string;
    domain: string;
    protocol: string;
    syncMode?: string | null;
    groupMappings?: unknown;
    scimTokenHash?: string | null;
    scimTokenExpiresAt?: number | null;
  };
  onGeneratedScimToken: (token: string | null) => void;
}) {
  const z = useZero();
  const multiIdp = useFeatureFlag(MULTI_IDP_SETTINGS_FLAG);

  const providerSyncMode: SyncMode =
    (provider.syncMode as SyncMode | null | undefined) ?? "off";

  const [config, setConfig] = useState({
    ...emptySsoConfig,
    protocol:
      provider.protocol === "saml" ? ("saml" as const) : ("oidc" as const),
    providerId: provider.providerId,
    issuer: provider.issuer,
    domain: provider.domain,
    syncMode: providerSyncMode,
  });
  const [baseline, setBaseline] = useState(() => JSON.stringify(config));
  const hasChanges = JSON.stringify(config) !== baseline;
  const issuerInvalid =
    config.protocol === "oidc" &&
    !!config.issuer.trim() &&
    ssoIssuerUrlError(config.issuer.trim()) !== null;

  async function handleSave(e: FormEvent) {
    e.preventDefault();

    const switchingToScim =
      config.syncMode === "scim" && providerSyncMode !== "scim";
    const switchingFromScim =
      providerSyncMode === "scim" && config.syncMode !== "scim";

    // Mint the token up front so every mutation below fires synchronously in
    // one atomic batch rather than being split by an await mid-save.
    const scim = switchingToScim ? await newScimToken() : null;

    if (switchingFromScim) {
      z.mutate(sharedMutators.revokeScimToken({ ssoProviderId: provider.id }));
    }

    z.mutate(
      sharedMutators.updateSSOProvider({
        id: provider.id,
        ...(config.issuer.trim() &&
          config.issuer.trim() !== provider.issuer && {
            issuer: config.issuer.trim(),
          }),
        ...(config.domain !== provider.domain && {
          domain: config.domain.trim().toLowerCase(),
        }),
        // Switching to scim is handled by generateScimToken below, which sets
        // syncMode together with the token hash.
        ...(config.syncMode !== providerSyncMode &&
          !switchingToScim && {
            syncMode: config.syncMode,
          }),
        ...(config.protocol === "saml"
          ? {
              ...(config.metadataUrl.trim() && {
                metadataUrl: config.metadataUrl.trim(),
              }),
              ...(config.metadataXml.trim() && {
                metadataXml: config.metadataXml.trim(),
              }),
              ...(config.entryPoint && {
                entryPoint: config.entryPoint,
              }),
              ...(config.cert && { cert: config.cert }),
              ...(jitUserProvisioningEnabled({ syncMode: config.syncMode }) &&
                (() => {
                  const { image, ...required } = config.mapping;
                  if (!Object.values(required).every((v) => v.trim()))
                    return {};
                  return {
                    mapping: {
                      email: required.email.trim(),
                      firstName: required.firstName.trim(),
                      lastName: required.lastName.trim(),
                      ...(image.trim() && { image: image.trim() }),
                    },
                  };
                })()),
            }
          : {
              ...(config.clientId && { clientId: config.clientId }),
              ...(config.clientSecret && {
                clientSecret: config.clientSecret,
              }),
              ...(config.scopes.trim() && {
                scopes: parseScopes(config.scopes),
              }),
            }),
      })
    );

    if (scim) {
      z.mutate(
        sharedMutators.generateScimToken({
          ssoProviderId: provider.id,
          tokenHash: scim.hash,
        })
      );
      onGeneratedScimToken(scim.token);
    } else if (switchingFromScim) {
      onGeneratedScimToken(null);
    }

    const resetConfig = {
      ...config,
      clientId: "",
      clientSecret: "",
      scopes: "",
      entryPoint: "",
      cert: "",
      metadataUrl: "",
      metadataXml: "",
      mapping: { ...emptyMapping },
    };
    setConfig(resetConfig);
    setBaseline(JSON.stringify(resetConfig));
  }

  return (
    <form onSubmit={(e) => void handleSave(e)}>
      <Section>
        <Field>
          <Box horizontal={8} vertical={10}>
            <Text variant="text-small-md">Protocol</Text>
          </Box>
          <Input
            value={provider.protocol.toUpperCase()}
            disabled
            style={disabledStyle}
          />
        </Field>

        {multiIdp && (
          <Field>
            <Box horizontal={8} vertical={10}>
              <Text variant="text-small-md">Provider ID</Text>
            </Box>
            <Input value={provider.providerId} disabled style={disabledStyle} />
          </Field>
        )}

        <SsoProviderFormFields
          existing
          config={config}
          onConfigChange={setConfig}
          scimTokenSection={
            providerSyncMode === "scim" ? (
              <ScimTokenSection
                providerId={provider.id}
                expiresAt={provider.scimTokenExpiresAt}
                onRevoked={() => {
                  const next = { ...config, syncMode: "off" as const };
                  setConfig(next);
                  setBaseline(JSON.stringify(next));
                }}
              />
            ) : undefined
          }
        />

        <FormActions>
          <Button
            variant="primary"
            size="l"
            type="submit"
            disabled={!hasChanges || issuerInvalid}
          >
            Save changes
          </Button>
        </FormActions>
      </Section>
    </form>
  );
}
